import { useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Save, ArrowLeft, AlertTriangle, Plus, Trash2 } from "lucide-react";
import { api } from "@/lib/api";
import { SectionTitle, Badge } from "@/components/Bits";

const TIMEFRAMES = ["1m", "3m", "5m", "15m", "30m", "1h"];
const LIQ_WINDOWS = ["1m", "3m", "5m", "15m"];
const DIRECTIONS = [
  { value: "any", label: "Any (bullish + bearish)" },
  { value: "bullish", label: "Bullish only" },
  { value: "bearish", label: "Bearish only" },
];

// Condition catalog — one block per available condition type
const CONDITION_CATALOG = {
  rsi_divergence: {
    label: "RSI Divergence",
    hint: "Bullish/bearish RSI divergence vs price pivots.",
    defaultParams: { rsi_period: 14, rsi_lookback: 50, max_alignment_distance: 7 },
  },
  rsi_absolute: {
    label: "RSI Absolute",
    hint: "RSI overbought/oversold trigger. Dual mode: one block fires bullish when RSI ≤ bullish threshold AND bearish when RSI ≥ bearish threshold.",
    defaultParams: {
      mode: "dual",
      rsi_period: 14,
      bullish_threshold: 30,
      bearish_threshold: 70,
      timeframe: "5m",
    },
  },
  cvd_divergence: {
    label: "CVD Divergence",
    hint: "Cumulative Volume Delta divergence — hidden pressure detector.",
    defaultParams: { cvd_lookback: 50, cvd_smoothing_length: 0, max_alignment_distance: 7 },
  },
  liquidation_filter: {
    label: "Liquidation Filter",
    hint: "Fires when liquidations on the chosen side exceed the threshold in the window.",
    defaultParams: { side: "auto", threshold_usdt: 50000000, window: "5m" },
  },
};

const PATCH_KEYS = [
  "name", "description", "timeframe", "conditions", "combinator",
  "condition_groups",
  "allow_long", "allow_short",
  "regime_direction_gate",
  "anti_reentry",
  "regime_strategy_router",
  "volatility_filter",
  "atr_exit",
  "regime_exposure",
  "cooldown_minutes",
];

function ConditionCard({ cond, idx, onChange, onRemove }) {
  const meta = CONDITION_CATALOG[cond.type] || { label: cond.type };
  const update = (patch) => onChange({ ...cond, ...patch });
  const updateParams = (patch) => onChange({ ...cond, params: { ...cond.params, ...patch } });

  return (
    <div
      data-testid={`cond-card-${idx}`}
      className={`rounded-sm border p-4 space-y-3 transition-colors ${
        cond.enabled ? "border-[#00E676]/30 bg-[#0F0F14]" : "border-border bg-[#0F0F14]/50 opacity-70"
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <Switch
            data-testid={`cond-enabled-${idx}`}
            checked={cond.enabled}
            onCheckedChange={(v) => update({ enabled: v })}
          />
          <div>
            <div className="text-sm font-semibold">{meta.label}</div>
            <div className="text-[11px] text-[#71717A]">{meta.hint}</div>
          </div>
        </div>
        <Button
          data-testid={`cond-remove-${idx}`}
          variant="outline"
          size="sm"
          onClick={onRemove}
          className="rounded-sm border-[#FF3B30]/40 bg-[#1A1A22] hover:bg-[#FF3B30]/10 text-[#FF3B30] text-xs"
        >
          <Trash2 size={12} strokeWidth={2} />
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Direction filter</Label>
          <Select
            value={cond.direction_filter || "any"}
            onValueChange={(v) => update({ direction_filter: v })}
          >
            <SelectTrigger data-testid={`cond-direction-${idx}`} className="bg-[#1A1A22] border-border rounded-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DIRECTIONS.map((d) => <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {cond.type === "rsi_divergence" ? (
          <>
            <div className="space-y-1">
              <Label className="text-xs">RSI period</Label>
              <Input
                type="number" min="2" max="200"
                value={cond.params?.rsi_period ?? 14}
                onChange={(e) => updateParams({ rsi_period: Number(e.target.value) })}
                className="bg-[#1A1A22] border-border rounded-sm font-mono"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Lookback (candles)</Label>
              <Input
                type="number" min="5" max="500"
                value={cond.params?.rsi_lookback ?? 50}
                onChange={(e) => updateParams({ rsi_lookback: Number(e.target.value) })}
                className="bg-[#1A1A22] border-border rounded-sm font-mono"
              />
            </div>
            <div className="space-y-1 md:col-span-2">
              <Label className="text-xs">
                Pivot alignment tolerance (candles)
              </Label>
              <Input
                data-testid={`cond-rsi-max-align-${idx}`}
                type="number" min="1" max="200"
                value={cond.params?.max_alignment_distance ?? 7}
                onChange={(e) => updateParams({ max_alignment_distance: Number(e.target.value) })}
                className="bg-[#1A1A22] border-border rounded-sm font-mono"
              />
              <p className="text-[10px] text-muted-foreground leading-snug">
                Max distance (in candles) allowed between a price pivot and its
                paired RSI pivot. Loosening (e.g. 12, 15, 20) accepts swings whose
                RSI peak/trough is offset from the price peak/trough; tightening
                (e.g. 5) only allows tightly-aligned divergences. Legacy default = 7.
              </p>
            </div>
          </>
        ) : null}

        {cond.type === "rsi_absolute" ? (() => {
          const mode = cond.params?.mode || (cond.params?.operator ? "single" : "dual");
          return (
          <>
            <div className="space-y-1">
              <Label className="text-xs">RSI period</Label>
              <Input
                type="number" min="2" max="200"
                value={cond.params?.rsi_period ?? 14}
                onChange={(e) => updateParams({ rsi_period: Number(e.target.value) })}
                className="bg-[#1A1A22] border-border rounded-sm font-mono"
              />
            </div>
            <div className="space-y-1 md:col-span-2">
              <Label className="text-xs">Mode</Label>
              <Select
                value={mode}
                onValueChange={(v) => {
                  if (v === "dual") {
                    updateParams({
                      mode: "dual",
                      bullish_threshold: cond.params?.bullish_threshold ?? 30,
                      bearish_threshold: cond.params?.bearish_threshold ?? 70,
                    });
                  } else {
                    updateParams({
                      mode: "single",
                      operator: cond.params?.operator || "lt",
                      value: cond.params?.value ?? 30,
                    });
                  }
                }}
              >
                <SelectTrigger data-testid={`cond-rsi-mode-${idx}`} className="bg-[#1A1A22] border-border rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="dual">Dual Threshold (recommended · separate bullish + bearish levels)</SelectItem>
                  <SelectItem value="single">Single Threshold (legacy · one operator + one value)</SelectItem>
                </SelectContent>
              </Select>
              <div className="text-[10px] text-[#71717A] mt-1">
                {mode === "dual"
                  ? "Fires LONG when RSI ≤ bullish threshold. Fires SHORT when RSI ≥ bearish threshold."
                  : "Fires in one direction only based on the chosen operator."}
              </div>
            </div>

            {mode === "dual" ? (
              <>
                <div className="space-y-1">
                  <Label className="text-xs">RSI Oversold Threshold (Bullish)</Label>
                  <Input
                    data-testid={`cond-rsi-bull-thr-${idx}`}
                    type="number" min="0" max="100" step="0.1"
                    value={cond.params?.bullish_threshold ?? 30}
                    onChange={(e) => updateParams({ bullish_threshold: Number(e.target.value) })}
                    className="bg-[#1A1A22] border-border rounded-sm font-mono"
                  />
                  <div className="text-[10px] text-[#71717A]">RSI ≤ this → LONG trigger</div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">RSI Overbought Threshold (Bearish)</Label>
                  <Input
                    data-testid={`cond-rsi-bear-thr-${idx}`}
                    type="number" min="0" max="100" step="0.1"
                    value={cond.params?.bearish_threshold ?? 70}
                    onChange={(e) => updateParams({ bearish_threshold: Number(e.target.value) })}
                    className="bg-[#1A1A22] border-border rounded-sm font-mono"
                  />
                  <div className="text-[10px] text-[#71717A]">RSI ≥ this → SHORT trigger</div>
                </div>
              </>
            ) : (
              <>
                <div className="space-y-1">
                  <Label className="text-xs">Operator</Label>
                  <Select
                    value={cond.params?.operator || "lt"}
                    onValueChange={(v) => updateParams({ operator: v })}
                  >
                    <SelectTrigger data-testid={`cond-operator-${idx}`} className="bg-[#1A1A22] border-border rounded-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="lt">&lt; (oversold → bullish)</SelectItem>
                      <SelectItem value="gt">&gt; (overbought → bearish)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Threshold value</Label>
                  <Input
                    data-testid={`cond-threshold-${idx}`}
                    type="number" min="0" max="100" step="0.1"
                    value={cond.params?.value ?? 30}
                    onChange={(e) => updateParams({ value: Number(e.target.value) })}
                    className="bg-[#1A1A22] border-border rounded-sm font-mono"
                  />
                </div>
              </>
            )}
          </>
          );
        })() : null}

        {cond.type === "cvd_divergence" ? (
          <>
            <div className="space-y-1">
              <Label className="text-xs">CVD lookback (candles)</Label>
              <Input
                data-testid="cvd-lookback-input"
                type="number" min="5" max="500"
                value={cond.params?.cvd_lookback ?? 50}
                onChange={(e) => updateParams({ cvd_lookback: Number(e.target.value) })}
                className="bg-[#1A1A22] border-border rounded-sm font-mono"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">
                CVD smoothing length (EMA, 0 = off)
              </Label>
              <Input
                data-testid="cvd-smoothing-length-input"
                type="number" min="0" max="200"
                value={cond.params?.cvd_smoothing_length ?? 0}
                onChange={(e) => updateParams({ cvd_smoothing_length: Number(e.target.value) })}
                className="bg-[#1A1A22] border-border rounded-sm font-mono"
              />
              <p className="text-[10px] text-muted-foreground leading-snug">
                Higher = broader/smoother swings · lower = more reactive ·
                0 keeps the raw Binance kline CVD series (legacy behaviour).
              </p>
            </div>
            <div className="space-y-1 md:col-span-2">
              <Label className="text-xs">
                Pivot alignment tolerance (candles)
              </Label>
              <Input
                data-testid="cvd-max-align-input"
                type="number" min="1" max="200"
                value={cond.params?.max_alignment_distance ?? 7}
                onChange={(e) => updateParams({ max_alignment_distance: Number(e.target.value) })}
                className="bg-[#1A1A22] border-border rounded-sm font-mono"
              />
              <p className="text-[10px] text-muted-foreground leading-snug">
                Max distance (in candles) allowed between a price pivot and its paired
                CVD pivot. CVD swings frequently lead/lag price by several bars on BTC 1m;
                try 12/15/20 if you see frequent <span className="font-mono">rejected_alignment</span>
                in logs. Tighter values (e.g. 5) demand near-perfect alignment. Legacy default = 7.
              </p>
            </div>
          </>
        ) : null}

        {cond.type === "liquidation_filter" ? (
          <>
            <div className="space-y-1">
              <Label className="text-xs">Liquidation side to watch</Label>
              <Select
                value={cond.params?.side || "auto"}
                onValueChange={(v) => updateParams({ side: v })}
              >
                <SelectTrigger
                  data-testid="liq-side-select"
                  className="bg-[#1A1A22] border-border rounded-sm"
                ><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto: long liqs → bullish, short liqs → bearish</SelectItem>
                  <SelectItem value="long">Long liquidations only → bullish only</SelectItem>
                  <SelectItem value="short">Short liquidations only → bearish only</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[10px] text-muted-foreground leading-snug">
                Auto evaluates both sides independently. Use Long-only / Short-only
                to restrict the condition to a single liquidation cascade.
              </p>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Threshold (USDT)</Label>
              <Input
                data-testid="liq-threshold-input"
                type="number" min="0"
                value={cond.params?.threshold_usdt ?? 50000000}
                onChange={(e) => updateParams({ threshold_usdt: Number(e.target.value) })}
                className="bg-[#1A1A22] border-border rounded-sm font-mono"
              />
            </div>
            <div className="space-y-1 md:col-start-1">
              <Label className="text-xs">Window</Label>
              <Select
                value={cond.params?.window || "5m"}
                onValueChange={(v) => updateParams({ window: v })}
              >
                <SelectTrigger className="bg-[#1A1A22] border-border rounded-sm"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {LIQ_WINDOWS.map((w) => <SelectItem key={w} value={w}>{w}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}

function RouterProfileGrid({ router, allProfiles, currentProfileId, onChange }) {
  // Helper widget for the Regime Strategy Router section. Renders 3
  // primary dropdowns (uptrend / downtrend / chop) + 2 optional
  // strong-regime dropdowns + 3 numeric risk multipliers. The
  // currentProfileId is excluded from each dropdown to prevent a
  // profile from routing to itself.
  const rsr = router || {};
  const mul = rsr.risk_multipliers || { uptrend: 1.0, downtrend: 1.0, chop: 0.5 };
  const eligible = (allProfiles || []).filter((p) => p && p.id !== currentProfileId);
  const update = (patch) => onChange({ ...rsr, ...patch });
  const updMul = (patch) =>
    onChange({ ...rsr, risk_multipliers: { ...mul, ...patch } });
  const disabled = !rsr.enabled;
  const Row = ({ label, idKey, mulKey, mulVal, placeholderMul }) => (
    <div className="grid grid-cols-[140px_1fr_70px_50px] items-center gap-2">
      <Label className="text-xs">{label}</Label>
      <select
        data-testid={`field-router-${idKey}`}
        className="text-xs bg-[#0F0F14] border border-[#52525B]/40 rounded-sm px-1 py-0.5 text-foreground"
        value={rsr[idKey] || ""}
        disabled={disabled}
        onChange={(e) => update({ [idKey]: e.target.value || null })}
      >
        <option value="">— (use owning profile)</option>
        {eligible.map((p) => (
          <option key={p.id} value={p.id}>{p.name}</option>
        ))}
      </select>
      <input
        data-testid={`field-router-mult-${mulKey}`}
        type="number" step="0.05" min={0} max={10}
        className="text-xs bg-[#0F0F14] border border-[#52525B]/40 rounded-sm px-1 py-0.5 text-foreground w-full"
        value={mulVal ?? placeholderMul}
        disabled={disabled}
        onChange={(e) => updMul({ [mulKey]: parseFloat(e.target.value) || 0 })}
      />
      <span className="text-[11px] text-[#71717A]">×size</span>
    </div>
  );
  return (
    <div className="space-y-2 pl-1">
      <Row label="UPTREND"          idKey="uptrend_profile_id"          mulKey="uptrend"          mulVal={mul.uptrend}   placeholderMul={1.0} />
      <Row label="DOWNTREND"        idKey="downtrend_profile_id"        mulKey="downtrend"        mulVal={mul.downtrend} placeholderMul={1.0} />
      <Row label="CHOP"             idKey="chop_profile_id"             mulKey="chop"             mulVal={mul.chop}      placeholderMul={0.5} />
      <Row label="STRONG_UPTREND"   idKey="strong_uptrend_profile_id"   mulKey="strong_uptrend"   mulVal={mul.strong_uptrend ?? ""}   placeholderMul={1.0} />
      <Row label="STRONG_DOWNTREND" idKey="strong_downtrend_profile_id" mulKey="strong_downtrend" mulVal={mul.strong_downtrend ?? ""} placeholderMul={1.0} />
    </div>
  );
}


export default function EntryRules() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [profiles, setProfiles] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [original, setOriginal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      // `Promise.allSettled` so a stray pre-auth 401 on either request
      // doesn't leave a sibling promise rejecting with no handler,
      // which would otherwise surface as a CRA dev-overlay banner.
      const [listRes, activeRes] = await Promise.allSettled([
        api.strategyProfiles(),
        api.strategyActive(),
      ]);
      const list = listRes.status === "fulfilled" ? (listRes.value || []) : [];
      const active = activeRes.status === "fulfilled" ? activeRes.value : null;
      setProfiles(list);
      setActiveId(active?.active_strategy_id || null);
      const fromUrl = searchParams.get("profile_id");
      const initial = list.find((p) => p.id === fromUrl)
        || list.find((p) => p.id === active?.active_strategy_id)
        || list[0] || null;
      if (initial) {
        setSelectedId(initial.id);
        setDraft({ ...initial });
        setOriginal({ ...initial });
        if (!fromUrl) setSearchParams({ profile_id: initial.id }, { replace: true });
      }
      setLoading(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSelectProfile = (id) => {
    const p = profiles.find((x) => x.id === id);
    if (!p) return;
    setSelectedId(id);
    setDraft({ ...p });
    setOriginal({ ...p });
    setSearchParams({ profile_id: id }, { replace: true });
  };

  const dirty = useMemo(() => {
    if (!draft || !original) return false;
    return PATCH_KEYS.some((k) => JSON.stringify(draft[k]) !== JSON.stringify(original[k]));
  }, [draft, original]);

  const addCondition = (type) => {
    const meta = CONDITION_CATALOG[type];
    if (!meta) return;
    const newCond = {
      id: crypto.randomUUID(),
      type,
      enabled: true,
      direction_filter: "any",
      params: { ...meta.defaultParams },
    };
    setDraft({ ...draft, conditions: [...(draft.conditions || []), newCond] });
  };

  const updateCondition = (idx, next) => {
    const copy = [...(draft.conditions || [])];
    copy[idx] = next;
    setDraft({ ...draft, conditions: copy });
  };

  const removeCondition = (idx) => {
    const copy = [...(draft.conditions || [])];
    copy.splice(idx, 1);
    setDraft({ ...draft, conditions: copy });
  };

  // ---------- Condition Groups (MVP) ----------
  // Grouped mode is active iff `condition_groups` is a non-empty array.
  // When OFF, the flat `conditions + combinator` UI/path is used (exact
  // legacy behaviour). When the user toggles ON, we seed Group 1 with
  // the current flat conditions so nothing is lost. Toggling OFF clears
  // groups (legacy path is fully restored).
  const groupsOn = Array.isArray(draft?.condition_groups) && draft.condition_groups.length > 0;

  const enableGroups = () => {
    // Seed: existing flat conditions become Group 1. New conditions
    // each get a fresh id so the group's reference list is stable
    // even if the user later renames the flat list (paranoid against
    // id collisions).
    const seedConditions = (draft.conditions || []).map((c) => ({
      ...c,
      id: c.id || (typeof crypto !== "undefined" ? crypto.randomUUID() : Math.random().toString(36).slice(2)),
    }));
    const seedGroup = {
      id: typeof crypto !== "undefined" ? crypto.randomUUID() : Math.random().toString(36).slice(2),
      name: "Setup A",
      enabled: true,
      conditions: seedConditions,
    };
    setDraft({ ...draft, condition_groups: [seedGroup] });
  };

  const disableGroups = () => {
    // Use [] instead of null because the backend's update_profile()
    // filters out None values from the patch (line 105 of
    // services/strategy_profiles.py — pre-existing behaviour shared by
    // every other field). `[]` is treated identically to None by the
    // evaluator (`_has_groups` returns False for empty arrays), so
    // legacy flat mode is restored.
    setDraft({ ...draft, condition_groups: [] });
  };

  const addGroup = () => {
    const idx = (draft.condition_groups || []).length;
    const letter = String.fromCharCode("A".charCodeAt(0) + idx);
    const g = {
      id: typeof crypto !== "undefined" ? crypto.randomUUID() : Math.random().toString(36).slice(2),
      name: `Setup ${letter}`,
      enabled: true,
      conditions: [],
    };
    setDraft({ ...draft, condition_groups: [...(draft.condition_groups || []), g] });
  };

  const updateGroup = (gIdx, patch) => {
    const groups = [...(draft.condition_groups || [])];
    groups[gIdx] = { ...groups[gIdx], ...patch };
    setDraft({ ...draft, condition_groups: groups });
  };

  const removeGroup = (gIdx) => {
    const groups = [...(draft.condition_groups || [])];
    groups.splice(gIdx, 1);
    // Removing the last group → drop back to flat mode for clarity.
    // Send `[]` (not null) so the backend persists the cleared state —
    // see `disableGroups` for the full rationale.
    setDraft({ ...draft, condition_groups: groups.length === 0 ? [] : groups });
  };

  const addConditionToGroup = (gIdx, type) => {
    const meta = CONDITION_CATALOG[type];
    if (!meta) return;
    const newCond = {
      id: typeof crypto !== "undefined" ? crypto.randomUUID() : Math.random().toString(36).slice(2),
      type, enabled: true, direction_filter: "any",
      params: { ...meta.defaultParams },
    };
    const groups = [...(draft.condition_groups || [])];
    const g = { ...groups[gIdx] };
    g.conditions = [...(g.conditions || []), newCond];
    groups[gIdx] = g;
    setDraft({ ...draft, condition_groups: groups });
  };

  const updateConditionInGroup = (gIdx, cIdx, next) => {
    const groups = [...(draft.condition_groups || [])];
    const g = { ...groups[gIdx] };
    const conds = [...(g.conditions || [])];
    conds[cIdx] = next;
    g.conditions = conds;
    groups[gIdx] = g;
    setDraft({ ...draft, condition_groups: groups });
  };

  const removeConditionFromGroup = (gIdx, cIdx) => {
    const groups = [...(draft.condition_groups || [])];
    const g = { ...groups[gIdx] };
    const conds = [...(g.conditions || [])];
    conds.splice(cIdx, 1);
    g.conditions = conds;
    groups[gIdx] = g;
    setDraft({ ...draft, condition_groups: groups });
  };
  // ----------------------------------------------------

  const onSave = async () => {
    if (!draft || !selectedId) return;
    // Strip legacy per-condition `timeframe` from rsi_absolute params: the
    // Strategy Profile timeframe is the single source of truth now. Applies
    // to both flat `conditions` and grouped `condition_groups[*].conditions`.
    const _stripTimeframe = (c) => {
      if (c?.type === "rsi_absolute" && c?.params && "timeframe" in c.params) {
        const { timeframe: _drop, ...rest } = c.params;
        return { ...c, params: rest };
      }
      return c;
    };
    if (Array.isArray(draft.conditions)) {
      draft.conditions = draft.conditions.map(_stripTimeframe);
    }
    if (Array.isArray(draft.condition_groups)) {
      draft.condition_groups = draft.condition_groups.map((g) => ({
        ...g,
        conditions: (g.conditions || []).map(_stripTimeframe),
      }));
    }
    const patch = {};
    PATCH_KEYS.forEach((k) => {
      if (JSON.stringify(draft[k]) !== JSON.stringify(original[k])) patch[k] = draft[k];
    });
    if (Object.keys(patch).length === 0) {
      toast.info("No changes to save");
      return;
    }
    try {
      setSaving(true);
      const updated = await api.strategyUpdate(selectedId, patch);
      setOriginal({ ...updated });
      setDraft({ ...updated });
      const fresh = await api.strategyProfiles();
      setProfiles(fresh || []);
      toast.success("Strategy saved" + (selectedId === activeId ? " · live logic updated" : ""));
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally { setSaving(false); }
  };

  if (loading) return <div className="p-6 text-sm text-[#A0A0AB]">Loading…</div>;
  if (profiles.length === 0) {
    return (
      <div className="p-6 max-w-3xl space-y-4">
        <SectionTitle sub="Entry Rules builds composable strategies from condition blocks.">
          Entry Rules
        </SectionTitle>
        <div className="bg-[#0F0F14] border border-border rounded-sm p-6 text-sm text-[#A0A0AB]">
          No strategy profiles yet. Create one from{" "}
          <button
            className="text-[#00E676] underline decoration-dotted hover:decoration-solid"
            onClick={() => navigate("/strategies")}
          >
            Strategy Profiles
          </button>.
        </div>
      </div>
    );
  }
  if (!draft) return null;

  const isActive = selectedId && selectedId === activeId;

  return (
    <div className="p-6 space-y-6 max-w-5xl">
      <SectionTitle
        sub="Build a composable strategy from condition blocks. Each condition is independently toggleable and parameterisable. Conditions combine via AND (all must agree) or OR (any can fire). If both directions fire together, the signal is suppressed (conflict-safe)."
        right={
          <Button variant="outline" size="sm" onClick={() => navigate("/strategies")}
            className="rounded-sm border-border bg-[#1A1A22] hover:bg-[#262633] text-xs">
            <ArrowLeft size={12} strokeWidth={2} className="mr-1" /> All strategies
          </Button>
        }
      >
        Entry Rules — Strategy Builder
      </SectionTitle>

      {/* Selector */}
      <div
        data-testid="strategy-selector-card"
        className={`rounded-sm border p-4 ${
          isActive ? "border-[#00E676]/40 bg-[#00E676]/5" : "border-border bg-[#0F0F14]"
        }`}
      >
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <Label className="text-[10px] uppercase tracking-widest text-[#71717A]">Editing Strategy</Label>
            <Select value={selectedId || undefined} onValueChange={onSelectProfile}>
              <SelectTrigger data-testid="strategy-selector" className="bg-[#1A1A22] border-border rounded-sm min-w-[260px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {profiles.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name} {p.id === activeId ? "• ACTIVE" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {isActive ? <Badge kind="on" testid="active-strategy-badge">ACTIVE STRATEGY</Badge> : null}
          </div>
          {dirty ? (
            <span className="text-xs text-[#F59E0B] flex items-center gap-1">
              <AlertTriangle size={12} strokeWidth={2} /> unsaved changes
            </span>
          ) : null}
        </div>
      </div>

      {/* Identity */}
      <div className="bg-[#0F0F14] border border-border rounded-sm p-5 space-y-4">
        <div className="text-[10px] uppercase tracking-widest text-[#71717A]">Identity</div>
        <div className="space-y-1">
          <Label className="text-xs">Name</Label>
          <Input
            data-testid="field-name"
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            className="bg-[#1A1A22] border-border rounded-sm"
          />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Description</Label>
          <Textarea
            data-testid="field-description"
            value={draft.description || ""}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            maxLength={400}
            className="bg-[#1A1A22] border-border rounded-sm text-xs min-h-[60px]"
          />
        </div>
      </div>

      {/* Combinator + Timing */}
      <div className="bg-[#0F0F14] border border-border rounded-sm p-5 space-y-4">
        <div className="text-[10px] uppercase tracking-widest text-[#71717A]">Combinator & Timing</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Combinator</Label>
            <Select
              value={draft.combinator}
              onValueChange={(v) => setDraft({ ...draft, combinator: v })}
              disabled={groupsOn}
            >
              <SelectTrigger data-testid="field-combinator" className="bg-[#1A1A22] border-border rounded-sm disabled:opacity-50">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="AND">AND · all conditions must agree</SelectItem>
                <SelectItem value="OR">OR · any condition can fire</SelectItem>
              </SelectContent>
            </Select>
            {groupsOn ? (
              <p className="text-[10px] text-[#71717A]">Ignored when Condition Groups are active (per-group AND, OR between groups).</p>
            ) : null}
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Strategy Timeframe — used by all conditions</Label>
            <Select value={draft.timeframe} onValueChange={(v) => setDraft({ ...draft, timeframe: v })}>
              <SelectTrigger data-testid="field-timeframe" className="bg-[#1A1A22] border-border rounded-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIMEFRAMES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-[#71717A]">Every condition (RSI Divergence, RSI Absolute, CVD Divergence) evaluates on these candles.</p>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Cooldown (min)</Label>
            <Input
              data-testid="field-cooldown"
              type="number" min="1" max="60"
              value={draft.cooldown_minutes}
              onChange={(e) => setDraft({ ...draft, cooldown_minutes: Number(e.target.value) })}
              className="bg-[#1A1A22] border-border rounded-sm font-mono"
            />
          </div>
        </div>
      </div>

      {/* Advanced: Condition Groups toggle.
          When OFF: legacy flat conditions UI (below) is shown — exact behaviour preserved.
          When ON: replaces the flat conditions UI with grouped blocks
          ((A AND B) OR (C)). Seeded with current flat list as Group 1. */}
      <div className="bg-[#0F0F14] border border-border rounded-sm p-4 flex items-center justify-between">
        <div className="space-y-1">
          <div className="text-xs flex items-center gap-2">
            <span>Advanced: Condition Groups</span>
            {groupsOn ? <Badge kind="long">ACTIVE</Badge> : null}
          </div>
          <div className="text-[11px] text-[#71717A] max-w-xl">
            Combine multiple independent setups in one strategy. Each group's conditions
            join with <span className="font-mono">AND</span>; groups are combined with <span className="font-mono">OR</span>.
            Example: <span className="font-mono">(RSI Div AND RSI Abs) OR (CVD Div)</span>.
            Disable to return to a single flat list.
          </div>
        </div>
        <Switch
          data-testid="field-groups-enabled"
          checked={groupsOn}
          onCheckedChange={(v) => v ? enableGroups() : disableGroups()}
        />
      </div>

      {/* Conditions (FLAT mode) — kept exactly as before. */}
      {!groupsOn ? (
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-widest text-[#71717A]">Conditions</div>
          <div className="flex items-center gap-2">
            {Object.entries(CONDITION_CATALOG).map(([type, meta]) => (
              <Button
                key={type}
                data-testid={`add-cond-${type}`}
                size="sm"
                variant="outline"
                onClick={() => addCondition(type)}
                className="rounded-sm border-border bg-[#1A1A22] hover:bg-[#262633] text-xs"
              >
                <Plus size={12} strokeWidth={2} className="mr-1" /> {meta.label}
              </Button>
            ))}
          </div>
        </div>

        {(draft.conditions || []).length === 0 ? (
          <div className="bg-[#0F0F14] border border-border rounded-sm p-6 text-sm text-[#71717A] text-center">
            No conditions yet. Add one from the buttons above.
          </div>
        ) : (
          <div data-testid="conditions-list" className="space-y-3">
            {draft.conditions.map((c, i) => (
              <ConditionCard
                key={c.id || i}
                cond={c}
                idx={i}
                onChange={(next) => updateCondition(i, next)}
                onRemove={() => removeCondition(i)}
              />
            ))}
          </div>
        )}
      </div>
      ) : (
      /* Conditions (GROUPED mode). */
      <div className="space-y-3" data-testid="groups-list">
        <div className="flex items-center justify-between">
          <div className="text-[10px] uppercase tracking-widest text-[#71717A]">
            Condition Groups <span className="normal-case tracking-normal text-[#71717A]">— joined by OR</span>
          </div>
          <Button
            data-testid="add-group-btn"
            size="sm"
            variant="outline"
            onClick={addGroup}
            className="rounded-sm border-[#00E676]/40 bg-[#1A1A22] hover:bg-[#262633] text-xs text-[#00E676]"
          >
            <Plus size={12} strokeWidth={2} className="mr-1" /> Add group
          </Button>
        </div>

        {(draft.condition_groups || []).map((g, gIdx) => (
          <div
            key={g.id || gIdx}
            data-testid={`group-card-${gIdx}`}
            className={`rounded-sm border p-4 space-y-3 ${
              g.enabled ? "border-[#3B82F6]/40 bg-[#0F0F14]" : "border-border bg-[#0F0F14]/50 opacity-70"
            }`}
          >
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 flex-1">
                <Switch
                  data-testid={`group-enabled-${gIdx}`}
                  checked={!!g.enabled}
                  onCheckedChange={(v) => updateGroup(gIdx, { enabled: v })}
                />
                <Input
                  data-testid={`group-name-${gIdx}`}
                  value={g.name || ""}
                  onChange={(e) => updateGroup(gIdx, { name: e.target.value.slice(0, 40) })}
                  placeholder={`Setup ${String.fromCharCode("A".charCodeAt(0) + gIdx)}`}
                  className="bg-[#1A1A22] border-border rounded-sm font-mono max-w-[200px] h-8 text-xs"
                />
                {/* Per-group timeframe — falls back to profile.timeframe
                    when empty. UI option value "__inherit__" maps to
                    null on save so the model stays clean. Inherited mode
                    is the safe default and matches every existing profile. */}
                <Select
                  value={g.timeframe || "__inherit__"}
                  onValueChange={(v) =>
                    updateGroup(gIdx, { timeframe: v === "__inherit__" ? null : v })
                  }
                >
                  <SelectTrigger
                    data-testid={`group-timeframe-${gIdx}`}
                    className="bg-[#1A1A22] border-border rounded-sm h-8 max-w-[180px] text-xs"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__inherit__">Inherit profile timeframe</SelectItem>
                    {TIMEFRAMES.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span className="text-[10px] uppercase tracking-widest text-[#71717A]">
                  internal AND · {(g.conditions || []).length} cond
                  {g.timeframe ? ` · ${g.timeframe}` : ""}
                </span>
              </div>
              <Button
                data-testid={`group-remove-${gIdx}`}
                size="sm"
                variant="ghost"
                onClick={() => removeGroup(gIdx)}
                className="text-[#F87171] hover:text-[#FCA5A5] hover:bg-[#F87171]/10 rounded-sm h-7 px-2"
              >
                <Trash2 size={12} strokeWidth={2} />
              </Button>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              {Object.entries(CONDITION_CATALOG).map(([type, meta]) => (
                <Button
                  key={type}
                  data-testid={`group-${gIdx}-add-cond-${type}`}
                  size="sm"
                  variant="outline"
                  onClick={() => addConditionToGroup(gIdx, type)}
                  className="rounded-sm border-border bg-[#1A1A22] hover:bg-[#262633] text-xs"
                >
                  <Plus size={12} strokeWidth={2} className="mr-1" /> {meta.label}
                </Button>
              ))}
            </div>

            {(g.conditions || []).length === 0 ? (
              <div className="bg-[#0B0B10] border border-border rounded-sm p-4 text-xs text-[#71717A] text-center">
                Empty group — add at least one condition or it cannot fire.
              </div>
            ) : (
              <div className="space-y-3">
                {g.conditions.map((c, cIdx) => (
                  <ConditionCard
                    key={c.id || cIdx}
                    cond={c}
                    idx={cIdx}
                    onChange={(next) => updateConditionInGroup(gIdx, cIdx, next)}
                    onRemove={() => removeConditionFromGroup(gIdx, cIdx)}
                  />
                ))}
              </div>
            )}
          </div>
        ))}

        {(draft.condition_groups || []).length > 1 ? (
          <div className="text-center text-[10px] uppercase tracking-widest text-[#71717A] py-1">
            ↑ joined by OR ↑
          </div>
        ) : null}
      </div>
      )}

      {/* Entry sides */}
      <div className="bg-[#0F0F14] border border-border rounded-sm p-5 space-y-4">
        <div className="text-[10px] uppercase tracking-widest text-[#71717A]">Entry sides</div>
        <div className="grid grid-cols-2 gap-4 max-w-md">
          <div className="flex items-center justify-between gap-2">
            <Label className="text-xs">Allow LONG</Label>
            <Switch
              data-testid="field-allow-long"
              checked={draft.allow_long}
              onCheckedChange={(v) => setDraft({ ...draft, allow_long: v })}
            />
          </div>
          <div className="flex items-center justify-between gap-2">
            <Label className="text-xs">Allow SHORT</Label>
            <Switch
              data-testid="field-allow-short"
              checked={draft.allow_short}
              onCheckedChange={(v) => setDraft({ ...draft, allow_short: v })}
            />
          </div>
        </div>

        {/* Regime Direction Gate — minimal opt-in toggle (P2-D UI hook).
            Disabled by default. Uses module defaults (EMA 50/200, ADX 14,
            chop/trend/strong 18/20/25, default mapping) — full editor
            ships in P2-E. */}
        <div className="border-t border-border/40 pt-4 mt-4 space-y-3">
          <div className="flex items-center gap-2">
            <Switch
              data-testid="field-regime-gate-enabled"
              checked={!!draft.regime_direction_gate?.enabled}
              onCheckedChange={(v) =>
                setDraft({
                  ...draft,
                  regime_direction_gate: {
                    ...(draft.regime_direction_gate || {}),
                    enabled: v,
                    regime_timeframe:
                      draft.regime_direction_gate?.regime_timeframe || "2h",
                  },
                })
              }
            />
            <Label className="text-xs">Regime Direction Gate (experimental)</Label>
            <select
              data-testid="field-regime-gate-tf"
              className="text-xs bg-[#0F0F14] border border-[#52525B]/40 rounded-sm px-1 py-0.5 text-foreground"
              value={draft.regime_direction_gate?.regime_timeframe || "2h"}
              disabled={!draft.regime_direction_gate?.enabled}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  regime_direction_gate: {
                    ...(draft.regime_direction_gate || {}),
                    regime_timeframe: e.target.value,
                  },
                })
              }
            >
              <option value="5m">5m</option>
              <option value="15m">15m</option>
              <option value="30m">30m</option>
              <option value="1h">1h</option>
              <option value="2h">2h (baseline)</option>
            </select>
          </div>
          <div className="text-[11px] text-[#71717A] leading-tight max-w-[640px]">
            Gates ENTRIES only. Default mapping: UPTREND → long only, DOWNTREND →
            short only, CHOP/NEUTRAL → both allowed. Opposite-signal EXITS are
            never blocked — raw bullish/bearish criteria still close open
            positions when the gate disagrees. Live the classifier output on
            the Dashboard "Market Regime" card before enabling.
          </div>
        </div>

        {/* Anti-Reentry Cooldown — post-loss directional veto.
            Promoted from research after the Phase-1+2 sweep validated
            `opposite_side_after_loss / 60m / any_loss` as the safe
            cross-strategy default. Disabled by default. Independent
            of `cooldown_minutes` (generic spacing). */}
        <div className="border-t border-border/40 pt-4 mt-4 space-y-3">
          <div className="flex items-center gap-2">
            <Switch
              data-testid="field-anti-reentry-enabled"
              checked={!!draft.anti_reentry?.enabled}
              onCheckedChange={(v) =>
                setDraft({
                  ...draft,
                  anti_reentry: {
                    ...(draft.anti_reentry || {}),
                    enabled: v,
                    variant: draft.anti_reentry?.variant || "opposite_side_after_loss",
                    cooldown_minutes: draft.anti_reentry?.cooldown_minutes || 60,
                    activation: draft.anti_reentry?.activation || "any_loss",
                  },
                })
              }
            />
            <Label className="text-xs">Anti-Reentry Cooldown</Label>
            <select
              data-testid="field-anti-reentry-variant"
              className="text-xs bg-[#0F0F14] border border-[#52525B]/40 rounded-sm px-1 py-0.5 text-foreground"
              value={draft.anti_reentry?.variant || "opposite_side_after_loss"}
              disabled={!draft.anti_reentry?.enabled}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  anti_reentry: {
                    ...(draft.anti_reentry || {}),
                    variant: e.target.value,
                  },
                })
              }
            >
              <option value="opposite_side_after_loss">opposite-side (Variant C — default)</option>
              <option value="same_side_after_loss">same-side (Variant A)</option>
              <option value="long_only_bearish">long-only bearish/CHOP (Variant B)</option>
              <option value="loss_streak_adaptive">adaptive streak (Variant D)</option>
            </select>
            <input
              data-testid="field-anti-reentry-cooldown"
              type="number"
              min={1}
              max={720}
              className="text-xs bg-[#0F0F14] border border-[#52525B]/40 rounded-sm px-1 py-0.5 text-foreground w-20"
              value={draft.anti_reentry?.cooldown_minutes ?? 60}
              disabled={!draft.anti_reentry?.enabled}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  anti_reentry: {
                    ...(draft.anti_reentry || {}),
                    cooldown_minutes: parseInt(e.target.value, 10) || 60,
                  },
                })
              }
            />
            <span className="text-[11px] text-[#71717A]">min</span>
            <select
              data-testid="field-anti-reentry-activation"
              className="text-xs bg-[#0F0F14] border border-[#52525B]/40 rounded-sm px-1 py-0.5 text-foreground"
              value={draft.anti_reentry?.activation || "any_loss"}
              disabled={!draft.anti_reentry?.enabled}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  anti_reentry: {
                    ...(draft.anti_reentry || {}),
                    activation: e.target.value,
                  },
                })
              }
            >
              <option value="any_loss">any loss</option>
              <option value="le_0_05">≤ −0.05%</option>
              <option value="le_0_10">≤ −0.10%</option>
              <option value="le_0_20">≤ −0.20%</option>
            </select>
          </div>
          <div className="text-[11px] text-[#71717A] leading-tight max-w-[640px]">
            Vetoes <strong>new entries only</strong> after a losing close.
            <em> Variant C (default)</em>: LONG loss blocks new SHORTs for the
            cooldown duration; SHORT loss blocks new LONGs.{" "}
            <strong>Never blocks closes</strong> (TP, SL, max-duration,
            opposite-signal, deterioration, emergency are unaffected).
            Independent of <code>cooldown_minutes</code> (which is generic
            spacing regardless of result) — both filters can coexist and
            whichever blocks first wins.
          </div>
        </div>

        {/* Regime Strategy Router — route entries to different saved
            profiles per regime. Owning profile keeps full control of
            exits, risk, cooldown, anti_reentry. Routed profiles
            contribute conditions / allow_long / allow_short only. */}
        <div className="border-t border-border/40 pt-4 mt-4 space-y-3">
          <div className="flex items-center gap-2">
            <Switch
              data-testid="field-router-enabled"
              checked={!!draft.regime_strategy_router?.enabled}
              onCheckedChange={(v) =>
                setDraft({
                  ...draft,
                  regime_strategy_router: {
                    ...(draft.regime_strategy_router || {}),
                    enabled: v,
                    regime_timeframe: draft.regime_strategy_router?.regime_timeframe || "2h",
                  },
                })
              }
            />
            <Label className="text-xs">Regime Strategy Router</Label>
            <select
              data-testid="field-router-tf"
              className="text-xs bg-[#0F0F14] border border-[#52525B]/40 rounded-sm px-1 py-0.5 text-foreground"
              value={draft.regime_strategy_router?.regime_timeframe || "2h"}
              disabled={!draft.regime_strategy_router?.enabled}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  regime_strategy_router: {
                    ...(draft.regime_strategy_router || {}),
                    regime_timeframe: e.target.value,
                  },
                })
              }
            >
              <option value="15m">15m</option>
              <option value="30m">30m</option>
              <option value="1h">1h</option>
              <option value="2h">2h (baseline)</option>
            </select>
          </div>
          <RouterProfileGrid
            router={draft.regime_strategy_router || {}}
            allProfiles={profiles}
            currentProfileId={draft.id}
            onChange={(rsr) =>
              setDraft({ ...draft, regime_strategy_router: rsr })
            }
          />
          <div className="text-[11px] text-amber-300/80 leading-tight max-w-[640px]">
            ⚠ Router affects <strong>new entries only</strong>. Exits always
            remain active. Routed profiles' own regime gate is bypassed (router
            is the regime selector). Risk multiplier scales order size at
            new-entry time only; existing positions are unaffected.
          </div>
        </div>

        {/* Volatility Filter — ATR-based veto on NEW entries only.
            Promoted from the 663-cell research sweep which proved
            block_long_only @ 1.5 % 2H ATR(14), gated to DOWNTREND
            regimes, adds ~$+32k Σ Eff PnL across the 13-preset book
            on top of the production Anti-Reentry baseline. SHORT-side
            and global blocking REJECTED by the research — SHORT
            volatility is alpha that MUST be preserved. Disabled by
            default. */}
        <div className="border-t border-border/40 pt-4 mt-4 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Switch
              data-testid="field-volatility-filter-enabled"
              checked={!!draft.volatility_filter?.enabled}
              onCheckedChange={(v) =>
                setDraft({
                  ...draft,
                  volatility_filter: {
                    ...(draft.volatility_filter || {}),
                    enabled: v,
                    mode: draft.volatility_filter?.mode || "block_long_only",
                    atr_timeframe: draft.volatility_filter?.atr_timeframe || "2h",
                    atr_period: draft.volatility_filter?.atr_period ?? 14,
                    atr_threshold_percent:
                      draft.volatility_filter?.atr_threshold_percent ?? 1.5,
                    apply_regimes:
                      draft.volatility_filter?.apply_regimes || [
                        "DOWNTREND",
                        "STRONG_DOWNTREND",
                      ],
                  },
                })
              }
            />
            <Label className="text-xs">Volatility Filter</Label>
            <select
              data-testid="field-volatility-filter-mode"
              className="text-xs bg-[#0F0F14] border border-[#52525B]/40 rounded-sm px-1 py-0.5 text-foreground"
              value={draft.volatility_filter?.mode || "block_long_only"}
              disabled={!draft.volatility_filter?.enabled}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  volatility_filter: {
                    ...(draft.volatility_filter || {}),
                    mode: e.target.value,
                  },
                })
              }
            >
              <option value="block_long_only">block LONG (default — research-validated)</option>
              <option value="block_short_only">block SHORT (⚠ destroys alpha)</option>
              <option value="block_global">block both sides</option>
              <option value="reduce_long_50">reduce LONG 50%</option>
              <option value="reduce_long_25">reduce LONG 25%</option>
              <option value="reduce_global_50">reduce both 50%</option>
            </select>
            <select
              data-testid="field-volatility-filter-tf"
              className="text-xs bg-[#0F0F14] border border-[#52525B]/40 rounded-sm px-1 py-0.5 text-foreground"
              value={draft.volatility_filter?.atr_timeframe || "2h"}
              disabled={!draft.volatility_filter?.enabled}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  volatility_filter: {
                    ...(draft.volatility_filter || {}),
                    atr_timeframe: e.target.value,
                  },
                })
              }
            >
              <option value="15m">15m</option>
              <option value="30m">30m</option>
              <option value="1h">1h</option>
              <option value="2h">2h (baseline)</option>
              <option value="4h">4h</option>
            </select>
            <input
              data-testid="field-volatility-filter-period"
              type="number"
              min={2}
              max={200}
              className="text-xs bg-[#0F0F14] border border-[#52525B]/40 rounded-sm px-1 py-0.5 text-foreground w-16"
              value={draft.volatility_filter?.atr_period ?? 14}
              disabled={!draft.volatility_filter?.enabled}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  volatility_filter: {
                    ...(draft.volatility_filter || {}),
                    atr_period: parseInt(e.target.value, 10) || 14,
                  },
                })
              }
            />
            <span className="text-[11px] text-[#71717A]">ATR period</span>
            <input
              data-testid="field-volatility-filter-threshold"
              type="number"
              step="0.1"
              min={0}
              max={100}
              className="text-xs bg-[#0F0F14] border border-[#52525B]/40 rounded-sm px-1 py-0.5 text-foreground w-20"
              value={draft.volatility_filter?.atr_threshold_percent ?? 1.5}
              disabled={!draft.volatility_filter?.enabled}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  volatility_filter: {
                    ...(draft.volatility_filter || {}),
                    atr_threshold_percent: parseFloat(e.target.value) || 0,
                  },
                })
              }
            />
            <span className="text-[11px] text-[#71717A]">% threshold</span>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <Label className="text-xs text-[#A0A0AB]">Apply to regimes:</Label>
            {["UPTREND", "STRONG_UPTREND", "CHOP", "DOWNTREND", "STRONG_DOWNTREND"].map(
              (label) => {
                const active = (
                  draft.volatility_filter?.apply_regimes || []
                ).includes(label);
                return (
                  <label
                    key={label}
                    data-testid={`field-volatility-filter-regime-${label.toLowerCase()}`}
                    className={`text-[11px] px-2 py-0.5 rounded-sm border cursor-pointer select-none ${
                      active
                        ? "bg-[#00E676]/15 border-[#00E676]/60 text-[#00E676]"
                        : "bg-[#0F0F14] border-[#52525B]/40 text-[#A0A0AB]"
                    } ${
                      !draft.volatility_filter?.enabled
                        ? "opacity-50 pointer-events-none"
                        : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      className="hidden"
                      checked={active}
                      disabled={!draft.volatility_filter?.enabled}
                      onChange={() => {
                        const current =
                          draft.volatility_filter?.apply_regimes || [];
                        const next = active
                          ? current.filter((r) => r !== label)
                          : [...current, label];
                        setDraft({
                          ...draft,
                          volatility_filter: {
                            ...(draft.volatility_filter || {}),
                            apply_regimes: next,
                          },
                        });
                      }}
                    />
                    {label}
                  </label>
                );
              }
            )}
            <span className="text-[11px] text-[#71717A]">
              (empty = always active)
            </span>
          </div>
          <div className="text-[11px] text-amber-300/80 leading-tight max-w-[680px]">
            ⚠ Research-validated default: <code>block_long_only / 2h ATR(14) ≥ 1.5%</code> /
            regimes <code>{"{DOWNTREND, STRONG_DOWNTREND}"}</code>. Vetoes{" "}
            <strong>new entries only</strong> — closes (TP, SL, max-duration,
            opposite-signal, deterioration, emergency) are never blocked.{" "}
            <strong>SHORT volatility is alpha</strong>: the 663-cell sweep
            proved <code>block_short_only</code> harms PnL by $4k–$25k across
            every strategy. Keep SHORT entries unfiltered unless you have a
            specific research reason. The filter ALWAYS combines with
            anti-reentry (the research baseline) — enable both together.
          </div>
        </div>

        {/* ATR Exit — production-promoted from the 1 911-cell
            refinement sweep (2026-02-27). Validated config:
            hybrid_mfe_activated / 2h ATR(14) / arm=2.0×ATR /
            trail=2.25×ATR. Default OFF — when enabled, ADDS one
            extra close opportunity per open position; NEVER blocks
            new entries; NEVER blocks any other exit (TP, SL,
            max-duration, opposite-signal, deterioration). */}
        <div className="border-t border-border/40 pt-4 mt-4 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Switch
              data-testid="field-atr-exit-enabled"
              checked={!!draft.atr_exit?.enabled}
              onCheckedChange={(v) =>
                setDraft({
                  ...draft,
                  atr_exit: {
                    ...(draft.atr_exit || {}),
                    enabled: v,
                    mode: draft.atr_exit?.mode || "hybrid_mfe_activated",
                    atr_timeframe: draft.atr_exit?.atr_timeframe || "2h",
                    atr_period: draft.atr_exit?.atr_period ?? 14,
                    arm_atr: draft.atr_exit?.arm_atr ?? 2.0,
                    trail_atr: draft.atr_exit?.trail_atr ?? 2.25,
                  },
                })
              }
            />
            <Label className="text-xs">ATR Exit</Label>
            <select
              data-testid="field-atr-exit-mode"
              className="text-xs bg-[#0F0F14] border border-[#52525B]/40 rounded-sm px-1 py-0.5 text-foreground"
              value={draft.atr_exit?.mode || "hybrid_mfe_activated"}
              disabled={!draft.atr_exit?.enabled}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  atr_exit: {
                    ...(draft.atr_exit || {}),
                    mode: e.target.value,
                  },
                })
              }
            >
              <option value="hybrid_mfe_activated">hybrid_mfe_activated (default — research-validated)</option>
            </select>
            <select
              data-testid="field-atr-exit-tf"
              className="text-xs bg-[#0F0F14] border border-[#52525B]/40 rounded-sm px-1 py-0.5 text-foreground"
              value={draft.atr_exit?.atr_timeframe || "2h"}
              disabled={!draft.atr_exit?.enabled}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  atr_exit: {
                    ...(draft.atr_exit || {}),
                    atr_timeframe: e.target.value,
                  },
                })
              }
            >
              <option value="30m">30m (rejected by research)</option>
              <option value="1h">1h (solid)</option>
              <option value="2h">2h (default — fully robust)</option>
              <option value="4h">4h</option>
            </select>
            <input
              data-testid="field-atr-exit-period"
              type="number"
              min={2}
              max={200}
              className="text-xs bg-[#0F0F14] border border-[#52525B]/40 rounded-sm px-1 py-0.5 text-foreground w-16"
              value={draft.atr_exit?.atr_period ?? 14}
              disabled={!draft.atr_exit?.enabled}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  atr_exit: {
                    ...(draft.atr_exit || {}),
                    atr_period: parseInt(e.target.value, 10) || 14,
                  },
                })
              }
            />
            <span className="text-[11px] text-[#71717A]">ATR period</span>
            <input
              data-testid="field-atr-exit-arm"
              type="number"
              step="0.05"
              min={0}
              max={10}
              className="text-xs bg-[#0F0F14] border border-[#52525B]/40 rounded-sm px-1 py-0.5 text-foreground w-20"
              value={draft.atr_exit?.arm_atr ?? 2.0}
              disabled={!draft.atr_exit?.enabled}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  atr_exit: {
                    ...(draft.atr_exit || {}),
                    arm_atr: parseFloat(e.target.value) || 0,
                  },
                })
              }
            />
            <span className="text-[11px] text-[#71717A]">arm × ATR</span>
            <input
              data-testid="field-atr-exit-trail"
              type="number"
              step="0.05"
              min={0.1}
              max={10}
              className="text-xs bg-[#0F0F14] border border-[#52525B]/40 rounded-sm px-1 py-0.5 text-foreground w-20"
              value={draft.atr_exit?.trail_atr ?? 2.25}
              disabled={!draft.atr_exit?.enabled}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  atr_exit: {
                    ...(draft.atr_exit || {}),
                    trail_atr: parseFloat(e.target.value) || 0,
                  },
                })
              }
            />
            <span className="text-[11px] text-[#71717A]">trail × ATR</span>
          </div>
          <div className="text-[11px] text-amber-300/80 leading-tight max-w-[680px]">
            ⚠ Research-validated C1: arms after{" "}
            <code>{draft.atr_exit?.arm_atr ?? 2.0}× entry ATR</code> MFE, then
            trails by{" "}
            <code>{draft.atr_exit?.trail_atr ?? 2.25}× entry ATR</code> using
            the <strong>frozen 2H ATR(14) at entry</strong> (never walks).
            Close-based trigger on the trade's timeframe; wicks don't fire.
            ADDS one close opportunity per position — <strong>never blocks
            new entries</strong>, never blocks TP / SL / max-duration /
            opposite-signal / deterioration. Refinement sweep (1 911 cells)
            shows the 2H grid delivering 16/16 double-win cells:{" "}
            <strong>Δ Eff PnL +$21k, Δ DD −3.7 pp, Δ Return/DD +15.6</strong>{" "}
            vs the AR + VF baseline.
          </div>
        </div>

        {/* Regime Exposure — production-promoted from operator's
            manual DD-driver validation. Validated defaults:
            UPTREND 2L/1S, DOWNTREND 1L/2S, CHOP 1L/1S/1T, spacing
            0.84%. Default OFF — when enabled, overrides the
            per-side caps + spacing based on the current classifier
            label at NEW-ENTRY time. STRONG_UPTREND inherits
            uptrend; STRONG_DOWNTREND inherits downtrend. NEVER
            blocks exits. */}
        <div className="border-t border-border/40 pt-4 mt-4 space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Switch
              data-testid="field-regime-exposure-enabled"
              checked={!!draft.regime_exposure?.enabled}
              onCheckedChange={(v) =>
                setDraft({
                  ...draft,
                  regime_exposure: {
                    ...(draft.regime_exposure || {}),
                    enabled: v,
                    spacing_percent: draft.regime_exposure?.spacing_percent ?? 0.84,
                    uptrend: draft.regime_exposure?.uptrend || {
                      max_long: 2, max_short: 1, max_total: null,
                    },
                    downtrend: draft.regime_exposure?.downtrend || {
                      max_long: 1, max_short: 2, max_total: null,
                    },
                    chop: draft.regime_exposure?.chop || {
                      max_long: 1, max_short: 1, max_total: 1,
                    },
                  },
                })
              }
            />
            <Label className="text-xs">Regime Exposure</Label>
            <input
              data-testid="field-regime-exposure-spacing"
              type="number"
              step="0.01"
              min={0}
              max={10}
              className="text-xs bg-[#0F0F14] border border-[#52525B]/40 rounded-sm px-1 py-0.5 text-foreground w-20"
              value={draft.regime_exposure?.spacing_percent ?? 0.84}
              disabled={!draft.regime_exposure?.enabled}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  regime_exposure: {
                    ...(draft.regime_exposure || {}),
                    spacing_percent: parseFloat(e.target.value) || 0,
                  },
                })
              }
            />
            <span className="text-[11px] text-[#71717A]">% spacing same-side</span>
          </div>

          {/* Per-regime grid */}
          {[
            { label: "UPTREND", key: "uptrend", showTotal: false,
              hint: "STRONG_UPTREND inherits these" },
            { label: "DOWNTREND", key: "downtrend", showTotal: false,
              hint: "STRONG_DOWNTREND inherits these" },
            { label: "CHOP", key: "chop", showTotal: true,
              hint: "max_total prevents stacking" },
          ].map((r) => (
            <div key={r.key} className="flex items-center gap-2 flex-wrap pl-6">
              <Label className="text-[11px] text-[#A0A0AB] w-28">
                {r.label}
              </Label>
              <span className="text-[11px] text-[#71717A]">long</span>
              <input
                data-testid={`field-regime-exposure-${r.key}-max-long`}
                type="number"
                min={0}
                max={20}
                className="text-xs bg-[#0F0F14] border border-[#52525B]/40 rounded-sm px-1 py-0.5 text-foreground w-16"
                value={draft.regime_exposure?.[r.key]?.max_long ??
                  (r.key === "uptrend" ? 2 : 1)}
                disabled={!draft.regime_exposure?.enabled}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    regime_exposure: {
                      ...(draft.regime_exposure || {}),
                      [r.key]: {
                        ...(draft.regime_exposure?.[r.key] || {}),
                        max_long: parseInt(e.target.value, 10),
                      },
                    },
                  })
                }
              />
              <span className="text-[11px] text-[#71717A]">short</span>
              <input
                data-testid={`field-regime-exposure-${r.key}-max-short`}
                type="number"
                min={0}
                max={20}
                className="text-xs bg-[#0F0F14] border border-[#52525B]/40 rounded-sm px-1 py-0.5 text-foreground w-16"
                value={draft.regime_exposure?.[r.key]?.max_short ??
                  (r.key === "downtrend" ? 2 : 1)}
                disabled={!draft.regime_exposure?.enabled}
                onChange={(e) =>
                  setDraft({
                    ...draft,
                    regime_exposure: {
                      ...(draft.regime_exposure || {}),
                      [r.key]: {
                        ...(draft.regime_exposure?.[r.key] || {}),
                        max_short: parseInt(e.target.value, 10),
                      },
                    },
                  })
                }
              />
              {r.showTotal && (
                <>
                  <span className="text-[11px] text-[#71717A]">total</span>
                  <input
                    data-testid={`field-regime-exposure-${r.key}-max-total`}
                    type="number"
                    min={0}
                    max={40}
                    className="text-xs bg-[#0F0F14] border border-[#52525B]/40 rounded-sm px-1 py-0.5 text-foreground w-16"
                    value={draft.regime_exposure?.[r.key]?.max_total ?? ""}
                    placeholder="—"
                    disabled={!draft.regime_exposure?.enabled}
                    onChange={(e) => {
                      const v = e.target.value;
                      setDraft({
                        ...draft,
                        regime_exposure: {
                          ...(draft.regime_exposure || {}),
                          [r.key]: {
                            ...(draft.regime_exposure?.[r.key] || {}),
                            max_total: v === "" ? null : parseInt(v, 10),
                          },
                        },
                      });
                    }}
                  />
                </>
              )}
              <span className="text-[11px] text-[#52525B] italic">{r.hint}</span>
            </div>
          ))}

          <div className="text-[11px] text-amber-300/80 leading-tight max-w-[680px]">
            ⚠ Validated default prevents counter-trend stacking. Strong
            regimes inherit (STRONG_UPTREND→uptrend, STRONG_DOWNTREND→
            downtrend). When the regime label is missing or unknown the
            engine FAILS SAFE to your existing{" "}
            <code>risk_settings.max_open_*</code> values.{" "}
            <strong>Exits are never blocked</strong> — TP, SL,
            max-duration, opposite-signal, deterioration, MFE-giveback,
            and ATR Exit all run unmodified.
          </div>
        </div>

        <div className="text-[11px] text-[#71717A] border-t border-border/40 pt-3">
          Auto-exits (TP, SL, max duration, opposite-signal close) live on the{" "}
          <button
            type="button"
            className="text-[#00E676] underline decoration-dotted hover:decoration-solid"
            onClick={() => navigate(`/exit-rules?profile_id=${selectedId}`)}
          >Exit Rules</button> page for this strategy.
        </div>
      </div>

      <div className="sticky bottom-4 bg-[#05050A]/80 backdrop-blur-sm border border-border rounded-sm p-3 flex items-center justify-between gap-3">
        <div className="text-xs text-[#A0A0AB]">
          {isActive ? (
            <span className="text-[#00E676]">Saving updates the live validation engine on the next cycle.</span>
          ) : (
            <>This profile is inactive. Save stores changes; activate it to affect automated execution.</>
          )}
        </div>
        <Button
          data-testid="save-profile-btn"
          onClick={onSave}
          disabled={saving || !dirty || !draft.name?.trim()}
          className="rounded-sm bg-[#00E676] hover:bg-[#00C766] text-black font-semibold disabled:opacity-50"
        >
          <Save size={14} strokeWidth={2} className="mr-1" />
          {saving ? "Saving…" : dirty ? "Save changes" : "No changes"}
        </Button>
      </div>
    </div>
  );
}
